// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundTerms } from '../services/fundsService.ts'
import { FundTermsRecord } from '../../../state_manager/data_models/fund_terms_record.ts'

/**
 * useFundTerms — fetches the fund's Terms-tab record (raw term/commitment/fee
 * columns + resolved effective dates) and exposes a refetch used after every
 * successful field patch so the effective dates re-resolve server-side.
 *
 * Terms live outside activeRecords: they are tab-local detail, not grid rows.
 */
export function useFundTerms(fundId: string | null | undefined) {
  const recordIdString = fundId ? String(fundId) : null

  const [terms, setTerms] = useState<FundTermsRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!recordIdString) {
      setTerms(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const res = await fetchFundTerms(recordIdString, accessToken)
      if (res.error) setError(res.error)
      setTerms(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load fund terms')
    } finally {
      setLoading(false)
    }
  }, [recordIdString])

  useEffect(() => {
    load()
  }, [load])

  return { terms, loading, error, refetch: load }
}
